import { useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { ThemeProvider } from "@mui/material";
import { useGetSignedAgreements } from "@src/appV2/Agreements/api/useGetSignedAgreements";
import { NoSignedAgreementFileAvailable } from "@src/appV2/Agreements/NoSignedAgreementFileAvailable";
import { AgreementKind } from "@src/appV2/Agreements/types";
import { getNewestSignedAgreementFromResponse } from "@src/appV2/Agreements/utils/getNewestSignedAgreementFromResponse";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { toFileNameSafeString } from "@src/appV2/lib/utils/toFileNameSafeString";
import { PdfViewerProvider } from "@src/appV2/PdfViewer/context";
import { PdfViewer } from "@src/appV2/PdfViewer/PdfViewer";
import { PdfViewerLoadingSkeleton } from "@src/appV2/PdfViewer/PdfViewerLoadingSkeleton";
import { Button } from "@src/appV2/redesign/components/Button";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useRef } from "react";

import { EmailAgreementBottomSheet } from "./EmailAgreementBottomSheet";

export function ICAgreementPage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const worker = useDefinedWorker();
  const emailModalState = useModalState();

  const {
    data: signedAgreements,
    isLoading: isLoadingAgreements,
    isError: isErrorAgreements,
    isSuccess: isSuccessAgreements,
  } = useGetSignedAgreements({
    workerId: worker.userId,
    kind: [AgreementKind.INDEPENDENT_CONTRACTOR],
  });

  const fileName = `${toFileNameSafeString(worker.name)}_contractor_agreement.pdf`;
  const pdfUrl = isSuccessAgreements
    ? getNewestSignedAgreementFromResponse(signedAgreements)?.attributes.downloadUrl
    : undefined;

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          title="Contractor Agreement"
          headerVariant="tertiary"
          scrollContainerRef={scrollRef}
          fallbackGoBackPath={DeprecatedGlobalAppV1Paths.PROFILE}
        />

        <PageContent>
          <PdfViewerProvider fileName={fileName} pdfUrl={pdfUrl}>
            {isLoadingAgreements && <PdfViewerLoadingSkeleton />}
            {isSuccessAgreements && !isDefined(pdfUrl) && <NoSignedAgreementFileAvailable />}
            {isErrorAgreements && <NoSignedAgreementFileAvailable />}
            {isSuccessAgreements && <PdfViewer />}
          </PdfViewerProvider>
        </PageContent>

        <PageFooterWrapper>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              emailModalState.openModal();
            }}
          >
            Email Me My Agreement
          </Button>
        </PageFooterWrapper>

        <EmailAgreementBottomSheet modalState={emailModalState} />
      </PageWrapper>
    </ThemeProvider>
  );
}
